import React from 'react';
import './DispatchedSummary.css';
import { filterWarehouseData } from '../utils/auth';

function DispatchedSummary({ data, onWarehouseClick, selectedWarehouses }) {
  if (!data) return null;

  // Filter warehouses based on user access
  const filteredWarehouses = filterWarehouseData(data.warehouses, 'warehouse');

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const formatCurrency = (num) => {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatWeight = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' lbs';
  };

  const handleCardClick = (warehouse, event) => {
    const isCtrlClick = event.ctrlKey || event.metaKey;
    onWarehouseClick(warehouse, isCtrlClick);
  };

  // Calculate totals for selected warehouses (using filtered data)
  const selectedTotals = selectedWarehouses.length > 0 ? {
    picks: filteredWarehouses
      .filter(w => selectedWarehouses.includes(w.warehouse))
      .reduce((sum, w) => sum + w.picks, 0),
    lines: filteredWarehouses
      .filter(w => selectedWarehouses.includes(w.warehouse))
      .reduce((sum, w) => sum + w.lines, 0),
    quantity: filteredWarehouses
      .filter(w => selectedWarehouses.includes(w.warehouse))
      .reduce((sum, w) => sum + w.quantity, 0),
    weight: filteredWarehouses
      .filter(w => selectedWarehouses.includes(w.warehouse))
      .reduce((sum, w) => sum + w.weight, 0),
    dollars: filteredWarehouses
      .filter(w => selectedWarehouses.includes(w.warehouse))
      .reduce((sum, w) => sum + w.dollars, 0)
  } : null;

  // Calculate grand totals across filtered warehouses
  const totalDaysToPick = filteredWarehouses.reduce((sum, w) => {
    // Each warehouse has avg_days_to_pick, so we need to get back total days: avg * picks
    return sum + (w.avg_days_to_pick || 0) * w.picks;
  }, 0);

  const grandTotals = {
    picks: filteredWarehouses.reduce((sum, w) => sum + w.picks, 0),
    lines: filteredWarehouses.reduce((sum, w) => sum + w.lines, 0),
    quantity: filteredWarehouses.reduce((sum, w) => sum + w.quantity, 0),
    weight: filteredWarehouses.reduce((sum, w) => sum + w.weight, 0),
    dollars: filteredWarehouses.reduce((sum, w) => sum + w.dollars, 0)
  };

  // Calculate overall average days to pick
  grandTotals.avg_days_to_pick = grandTotals.picks > 0 ? totalDaysToPick / grandTotals.picks : 0;

  return (
    <div className="dispatched-summary">
      <div className="summary-header">
        <h3>Warehouse Performance Overview</h3>
        <p className="date-range">{data.dateRange}</p>
        <p className="instructions">
          Click a warehouse for picker details • Ctrl+Click to select multiple for comparison
        </p>
      </div>

      {/* Grand Totals - All Warehouses */}
      <div className="grand-totals-card">
        <h4>All Warehouses - Total</h4>
        <div className="totals-grid">
          <div className="total-item">
            <span className="total-label">Avg Days to Pick:</span>
            <span className="total-value">{grandTotals.avg_days_to_pick?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Picks:</span>
            <span className="total-value">{formatNumber(grandTotals.picks)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Lines:</span>
            <span className="total-value">{formatNumber(grandTotals.lines)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Quantity:</span>
            <span className="total-value">{formatNumber(grandTotals.quantity)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Weight:</span>
            <span className="total-value">{formatWeight(grandTotals.weight)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Dollars:</span>
            <span className="total-value">{formatCurrency(grandTotals.dollars)}</span>
          </div>
        </div>
      </div>

      {selectedTotals && (
        <div className="selected-totals-card">
          <h4>Selected Warehouses Totals ({selectedWarehouses.length})</h4>
          <div className="totals-grid">
            <div className="total-item">
              <span className="total-label">Total Picks:</span>
              <span className="total-value">{formatNumber(selectedTotals.picks)}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Total Lines:</span>
              <span className="total-value">{formatNumber(selectedTotals.lines)}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Total Quantity:</span>
              <span className="total-value">{formatNumber(selectedTotals.quantity)}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Total Weight:</span>
              <span className="total-value">{formatWeight(selectedTotals.weight)}</span>
            </div>
            <div className="total-item">
              <span className="total-label">Total Dollars:</span>
              <span className="total-value">{formatCurrency(selectedTotals.dollars)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="warehouse-grid">
        {filteredWarehouses.sort((a, b) => a.warehouse.localeCompare(b.warehouse)).map((warehouse) => {
          const isSelected = selectedWarehouses.includes(warehouse.warehouse);
          return (
            <div
              key={warehouse.warehouse}
              className={`warehouse-card ${isSelected ? 'selected' : ''}`}
              onClick={(e) => handleCardClick(warehouse.warehouse, e)}
            >
              <div className="warehouse-header">
                <h3>{warehouse.warehouse}</h3>
                <span className="country-badge">{warehouse.country}</span>
              </div>

              <div className="warehouse-metrics">
                <div className="metric">
                  <span className="metric-label">Avg Days to Pick</span>
                  <span className="metric-value">{warehouse.avg_days_to_pick?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Picks</span>
                  <span className="metric-value">{formatNumber(warehouse.picks)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Lines</span>
                  <span className="metric-value">{formatNumber(warehouse.lines)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Quantity</span>
                  <span className="metric-value">{formatNumber(warehouse.quantity)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Weight</span>
                  <span className="metric-value">{formatWeight(warehouse.weight)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Dollars</span>
                  <span className="metric-value">{formatCurrency(warehouse.dollars)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DispatchedSummary;
